// TOBE 네이티브 브리지 — Capacitor 플러그인을 기능 감지 후 사용. 웹(브라우저)에서는 자동으로 기존 동작으로 폴백.
import { Capacitor } from "@capacitor/core";

export const isNative = () => { try { return Capacitor.isNativePlatform(); } catch (e) { return false; } };

// ★네이티브인데 "플러그인이 쓸 수 있는" 상태인지까지 본다.
// 서비스워커가 첫 화면을 가로챈 비정상 기동에서는 isNative()=참인데 플러그인이 주입되지 않아
// Filesystem/Share 가 'not implemented' 예외를 낸다. isPluginAvailable 이 그 상태를 갈라 준다.
export const canNativeSave = () => {
  try { return isNative() && Capacitor.isPluginAvailable("Filesystem") && Capacitor.isPluginAvailable("Share"); }
  catch (e) { return false; }
};

function blobToBase64(blob) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onloadend = () => res(String(r.result).split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(blob);
  });
}

// ── 안드로이드 하드웨어 뒤로가기: 앱이 등록한 핸들러 우선, 없으면 종료 ──
let backHandler = null;
export function setBackHandler(fn) { backHandler = fn; }

export async function initNative() {
  if (!isNative()) return;
  try { const { StatusBar, Style } = await import("@capacitor/status-bar"); await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {}); await StatusBar.setStyle({ style: Style.Light }); await StatusBar.setBackgroundColor({ color: "#ffffff" }).catch(() => {}); } catch (e) {}
  try { const { SplashScreen } = await import("@capacitor/splash-screen"); await SplashScreen.hide(); } catch (e) {}
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("backButton", () => {
      try { if (backHandler && backHandler()) return; } catch (e) {}
      try { App.exitApp(); } catch (e) {}
    });
  } catch (e) {}
  try { const { LocalNotifications } = await import("@capacitor/local-notifications"); await LocalNotifications.requestPermissions(); } catch (e) {}
  try { const { Network } = await import("@capacitor/network"); Network.addListener("networkStatusChange", (st) => { try { window.dispatchEvent(new CustomEvent("tobe-net", { detail: !!st.connected })); } catch (e) {} }); } catch (e) {}
}

// ── 문서/파일: 네이티브면 폰에 저장 후 '열기/공유' 시트, 웹이면 일반 다운로드 ──
export async function deliverFile(blob, filename, mimeType) {
  if (!isNative()) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename || "download";
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    return;
  }
  // 네이티브인데 플러그인이 주입 안 된 비정상 기동:
  // 여기서 웹 방식(blob a[download])으로 물러서 봐야 앱 WebView 엔 DownloadListener 가 없어 조용히 무시된다
  // (2026-09-09 여섯 번 눌러도 아무 일 없던 그 상태다). 조용히 끝내지 말고 껐다 켜라고 안내한다
  // (index.js 가 다음 실행부터 서비스워커를 해제하므로 재실행하면 정상 기동으로 풀린다).
  if (!canNativeSave()) {
    try { window.alert("앱이 파일 저장 기능을 아직 불러오지 못했습니다.\n앱을 완전히 닫았다가 다시 연 뒤 한 번만 더 눌러 주세요."); } catch (e) {}
    return;
  }
  try {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const data = await blobToBase64(blob);
    const safe = (filename || "file").replace(/[\\/:*?"<>|]/g, "_");
    // 폰에서 사람이 찾아갈 수 있는 자리(문서함)에 먼저 둔다. 안 되면 앱 임시폴더로 물러선다.
    let w = null, where = "폰의 문서함";
    try { w = await Filesystem.writeFile({ path: safe, data, directory: Directory.Documents }); }
    catch (e1) { w = await Filesystem.writeFile({ path: safe, data, directory: Directory.Cache }); where = "앱 임시폴더"; }
    // ★공유 시트가 안 뜨는 폰이 있다. 예전엔 그 오류를 통째로 삼켜서 "눌러도 아무 일이 없다"가 됐다
    //   (2026-09-09 사장님이 같은 문서 내려받기를 네 번 눌렀다). 취소와 진짜 실패를 갈라 적는다.
    try {
      await Share.share({ title: safe, url: w.uri });
    } catch (e) {
      const m = String((e && (e.message || e)) || "");
      if (!/cancel|abort|dismiss|취소/i.test(m)) {
        try { window.alert(safe + " 파일을 " + where + "에 저장했습니다. (공유 창은 열지 못했습니다: " + m.slice(0, 60) + ")"); } catch (e2) {}
      }
    }
  } catch (e) {
    // 네이티브 저장이 통째로 실패 → 웹 방식으로 한 번 더 시도한다. 빈손으로 끝내지 않는다.
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename || "download";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e2) {
      try { window.alert("파일을 저장하지 못했습니다: " + String((e && (e.message || e)) || "")); } catch (e3) {}
    }
  }
}

// ── 일정 → 잠금화면 로컬 알림(시작 시각 + 10분 전) ──
export async function syncScheduleReminders(schedules) {
  if (!isNative()) return;
  let LocalNotifications;
  try { ({ LocalNotifications } = await import("@capacitor/local-notifications")); } catch (e) { return; }
  try { const p = await LocalNotifications.requestPermissions(); if (p.display !== "granted") return; } catch (e) { return; }
  const now = Date.now();
  const notifs = [];
  (schedules || []).forEach((s) => {
    const raw = s.start_at || s.start || s.date; if (!raw) return;
    const t = new Date(String(raw).replace(" ", "T")); const at = t.getTime();
    if (isNaN(at) || at <= now) return;
    const base = 100000 + ((parseInt(s.id, 10) || 0) % 800000) * 2;
    notifs.push({ id: base, title: "📅 일정", body: s.title || "일정이 시작됩니다", schedule: { at: t } });
    const pre = new Date(at - 10 * 60000);
    if (pre.getTime() > now) notifs.push({ id: base + 1, title: "📅 10분 후 일정", body: s.title || "일정", schedule: { at: pre } });
  });
  if (notifs.length) { try { await LocalNotifications.schedule({ notifications: notifs.slice(0, 60) }); } catch (e) {} }
}

// ── 네이티브 카메라 촬영 → File 반환(첨부/명함 촬영용) ──
export async function capturePhoto() {
  if (!isNative()) return null;
  try {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const photo = await Camera.getPhoto({ quality: 82, resultType: CameraResultType.DataUrl, source: CameraSource.Camera, allowEditing: false });
    const res = await fetch(photo.dataUrl); const blob = await res.blob();
    return new File([blob], `photo_${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
  } catch (e) { return null; }
}

// ── 폰 주소록에서 한 명 선택 → {name, phone, email} ──
export async function pickContact() {
  if (!isNative()) return null;
  try {
    const { Contacts } = await import("@capacitor-community/contacts");
    const perm = await Contacts.requestPermissions(); if (perm.contacts !== "granted") { window.alert("주소록 권한이 필요합니다."); return null; }
    const r = await Contacts.pickContact({ projection: { name: true, phones: true, emails: true } });
    const c = r && r.contact; if (!c) return null;
    return {
      name: (c.name && (c.name.display || [c.name.given, c.name.family].filter(Boolean).join(" "))) || "",
      phone: (c.phones && c.phones[0] && c.phones[0].number) || "",
      email: (c.emails && c.emails[0] && c.emails[0].address) || "",
    };
  } catch (e) { return null; }
}

export async function haptic() {
  if (!isNative()) return;
  try { const { Haptics, ImpactStyle } = await import("@capacitor/haptics"); await Haptics.impact({ style: ImpactStyle.Medium }); } catch (e) {}
}

// ── 온디바이스 OCR(ML Kit, 폰 안에서 즉시 인식) — File/Blob → 텍스트 ──
export async function ocrImage(fileOrBlob) {
  if (!isNative() || !fileOrBlob) return null;
  try {
    const { CapacitorPluginMlKitTextRecognition } = await import("@pantrist/capacitor-plugin-ml-kit-text-recognition");
    const base64 = await blobToBase64(fileOrBlob);
    const r = await CapacitorPluginMlKitTextRecognition.detectText({ base64Image: base64 });
    return (r && typeof r.text === "string") ? r.text : "";
  } catch (e) { return null; }
}

// ── 통화 중 화면 꺼짐 방지(Screen Wake Lock — 웹뷰 지원, 플러그인 불필요) ──
let _wakeLock = null;
export async function keepAwake() {
  try { if ("wakeLock" in navigator) { _wakeLock = await navigator.wakeLock.request("screen"); } } catch (e) {}
}
export async function allowSleep() {
  try { if (_wakeLock) { await _wakeLock.release(); _wakeLock = null; } } catch (e) {}
}

// ── 즉시 로컬 알림(중요메일·수신전화 등, 앱이 살아있을 때) ──
export async function notify(title, body, id) {
  if (!isNative()) return;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.schedule({ notifications: [{ id: id || (Math.floor(Math.random() * 2000000000)), title, body: body || "", schedule: { at: new Date(Date.now() + 200) } }] });
  } catch (e) {}
}

// ── 네이티브 음성 받아쓰기(@capacitor-community/speech-recognition) ──
// 네이티브 앱 WebView는 Web Speech API(webkitSpeechRecognition)를 지원하지 않으므로(iOS는 전혀,
// Android도 대체로 미지원) 앱에서는 이 네이티브 플러그인 경로로 받아쓰기를 처리한다.
// 웹(브라우저)에서는 isNative()=false 라 null을 돌려주고, 호출부가 Web Speech API로 폴백한다.
//
//   onPartial(text): 말하는 동안 부분 인식 결과마다 호출
//   onEnd():         인식 종료 시 1회 호출
//   반환: 듣기를 멈추는 async stop 함수(시작 실패/미지원이면 null)
export async function startNativeDictation(onPartial, onEnd) {
  if (!isNative()) return null;
  let SpeechRecognition;
  try { ({ SpeechRecognition } = await import("@capacitor-community/speech-recognition")); }
  catch (e) { return null; }  // 플러그인 미설치 → 호출부가 폴백
  try {
    const avail = await SpeechRecognition.available();
    if (avail && avail.available === false) return null;
  } catch (e) { /* available 미구현 버전은 통과 */ }
  try {
    const perm = await SpeechRecognition.requestPermissions();
    if (perm && perm.speechRecognition && perm.speechRecognition !== "granted") {
      try { window.alert("음성 인식 권한이 필요합니다. 설정에서 마이크/음성 인식을 허용해 주세요."); } catch (e2) {}
      return null;
    }
  } catch (e) { /* requestPermissions 미구현이면 start 단계에서 권한 요청됨 */ }
  try {
    try { await SpeechRecognition.removeAllListeners(); } catch (e) {}
    await SpeechRecognition.addListener("partialResults", (data) => {
      const m = (data && (data.matches || data.value)) || [];
      if (m.length && typeof onPartial === "function") onPartial(m[0]);
    });
    await SpeechRecognition.addListener("listeningState", (st) => {
      if (st && st.status === "stopped" && typeof onEnd === "function") onEnd();
    });
    // partialResults=true → 결과는 리스너로 들어오고, start는 즉시 반환됨
    await SpeechRecognition.start({ language: "ko-KR", partialResults: true, popup: false });
  } catch (e) {
    try { await SpeechRecognition.removeAllListeners(); } catch (e2) {}
    return null;
  }
  return async () => {
    try { await SpeechRecognition.stop(); } catch (e) {}
    try { await SpeechRecognition.removeAllListeners(); } catch (e) {}
    if (typeof onEnd === "function") onEnd();
  };
}

// ── 통화 푸시(FCM) 등록 + 수신 ──
// 앱이 발급받은 FCM 토큰을 onToken(token, platform)으로 넘겨 백엔드에 등록하고,
// 전화 알림 수신/탭 시 'tobe-call-push' 윈도 이벤트를 발생시킨다(앱이 전화 화면으로 이동).
// 웹/플러그인 미설치/비네이티브면 조용히 no-op(google-services.json + APK 재빌드 후 동작).
export async function registerCallPush(onToken) {
  if (!isNative()) return;
  let PushNotifications;
  try { ({ PushNotifications } = await import("@capacitor/push-notifications")); }
  catch (e) { return; }  // 플러그인 미설치
  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;
    await PushNotifications.removeAllListeners();
    PushNotifications.addListener("registration", (t) => {
      try { onToken && onToken(t.value, Capacitor.getPlatform()); } catch (e) {}
    });
    PushNotifications.addListener("registrationError", () => {});
    // 전화 알림 채널(안드로이드 8+ 필수: 채널 없으면 알림이 안 뜸). 높은 중요도=헤드업/소리.
    try { await PushNotifications.createChannel({ id: "tobe_calls", name: "전화", description: "수신 전화 알림", importance: 5, visibility: 1, sound: "default" }); } catch (e) {}
    // 포그라운드 수신
    PushNotifications.addListener("pushNotificationReceived", (n) => {
      try { window.dispatchEvent(new CustomEvent("tobe-call-push", { detail: { data: (n && n.data) || {}, foreground: true } })); } catch (e) {}
    });
    // 알림 탭(백그라운드/종료 상태에서 진입)
    PushNotifications.addListener("pushNotificationActionPerformed", (a) => {
      try { window.dispatchEvent(new CustomEvent("tobe-call-push", { detail: { data: (a && a.notification && a.notification.data) || {}, tapped: true } })); } catch (e) {}
    });
    await PushNotifications.register();
  } catch (e) { /* 등록 실패는 조용히 무시 */ }
}

// ── 생체 인증(지문/얼굴) 앱잠금 ──
export async function biometricAvailable() {
  if (!isNative()) return false;
  try { const { NativeBiometric } = await import("capacitor-native-biometric"); const r = await NativeBiometric.isAvailable(); return !!(r && r.isAvailable); } catch (e) { return false; }
}
export async function biometricVerify(reason) {
  if (!isNative()) return true;
  try {
    const { NativeBiometric } = await import("capacitor-native-biometric");
    await NativeBiometric.verifyIdentity({ reason: reason || "TOBE 잠금 해제", title: "TOBE 잠금 해제", subtitle: "지문 또는 얼굴로 인증", description: "" });
    return true;
  } catch (e) { return false; }
}
